import React from 'react'
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
} from '@mui/material'
import { useAuthenticatedUser } from 'contexts'
import { Company } from 'models'
import { ALL_ACCOUNTS_ID, useCompanies } from 'contexts/CompaniesContext'

const useSellerSelection = () => {
  const { isLoginB2C } = useAuthenticatedUser()
  const {
    selectedParent,
    childrenCompanies,
    pendingSelectionState,
    validatedSelectionState,
    isOrphanSelected,
    setPendingSelection,
    validateSelection,
    hasPendingSelection,
    pendingSelectionLimit,
    pendingSelectionLimitReached,
    canSelectAllChildren,
    isChildrenLoading,
    isAllAccountsSelected,
    isB2CNoFamily,
    setChildrenSearchValue,
    setChildrenSearchFilterValue,
    childrenSearchValue,
  } = useCompanies()

  const childOptions = React.useMemo(
    () => childrenCompanies.filter((company) => company.id !== ALL_ACCOUNTS_ID),
    [childrenCompanies],
  )
  const allAccountsOption = React.useMemo(
    () => childrenCompanies.find((company) => company.id === ALL_ACCOUNTS_ID),
    [childrenCompanies],
  )

  const handlePendingSelectionChange = React.useCallback(
    (
      _event: React.SyntheticEvent<Element, Event>,
      value: Array<Company>,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<Company>,
    ) => {
      if (reason === 'clear') {
        setPendingSelection([])
        return
      }

      const selectionWithoutAllAccounts = value.filter((company) => company.id !== ALL_ACCOUNTS_ID)

      if (details?.option?.id === ALL_ACCOUNTS_ID) {
        if (reason === 'selectOption') {
          if (!canSelectAllChildren) {
            return
          }
          setPendingSelection(childOptions, { isAllAccountsSelected: true })
          return
        }

        if (reason === 'removeOption') {
          setPendingSelection(selectionWithoutAllAccounts, { isAllAccountsSelected: false })
          return
        }
      }

      const shouldUseAllAccounts =
        canSelectAllChildren && selectionWithoutAllAccounts.length === childOptions.length
      setPendingSelection(selectionWithoutAllAccounts, {
        isAllAccountsSelected: shouldUseAllAccounts,
      })
    },
    [canSelectAllChildren, childOptions, setPendingSelection],
  )

  const handleChildrenSearchInputChange = React.useCallback(
    (
      _event: React.SyntheticEvent<Element, Event>,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason === 'input') {
        setChildrenSearchValue(value)
        return
      }

      if (reason === 'clear') {
        setChildrenSearchValue('', { skipCommit: true })
        setChildrenSearchFilterValue('')
      }
    },
    [setChildrenSearchFilterValue, setChildrenSearchValue],
  )

  const getOptionDisabled = React.useCallback(
    (option: Company) => {
      if (pendingSelectionState.mode === 'allAccounts' && !canSelectAllChildren) {
        return true
      }

      if (pendingSelectionLimitReached) {
        return !pendingSelectionState.items.some((company) => company.id === option.id)
      }

      return false
    },
    [canSelectAllChildren, pendingSelectionLimitReached, pendingSelectionState],
  )

  const childSelectorParams = React.useMemo(
    () => ({
      childrenCompanies,
      pendingSelectionState,
      validatedSelectionState,
      isAllAccountsSelected,
      isChildSelectorDisabled: !isOrphanSelected && !selectedParent,
      pendingSelectionLimit,
      pendingSelectionLimitReached,
      hasPendingSelection,
      allAccountsOption,
      isOrphanSelected,
      childrenSearchValue,
      canSelectAllChildren,
      isChildrenLoading,
      onSelectionChange: handlePendingSelectionChange,
      onInputChange: handleChildrenSearchInputChange,
      getOptionDisabled,
    }),
    [
      allAccountsOption,
      canSelectAllChildren,
      childrenCompanies,
      childrenSearchValue,
      getOptionDisabled,
      handleChildrenSearchInputChange,
      handlePendingSelectionChange,
      hasPendingSelection,
      isAllAccountsSelected,
      isChildrenLoading,
      isOrphanSelected,
      pendingSelectionLimit,
      pendingSelectionLimitReached,
      pendingSelectionState,
      selectedParent,
      validatedSelectionState,
    ],
  )

  const shouldHideSellerSelection = isLoginB2C && isB2CNoFamily

  return {
    shouldHideSellerSelection,
    isOrphanSelected,
    familySelectorParams: {
      isLoginB2C,
      selectedParent,
    },
    childSelectorParams,
    validationParams: {
      hasPendingSelection,
      isChildrenLoading,
      onValidate: validateSelection,
    },
  }
}

export { useSellerSelection }
